import {
  BenefitMetrics,
  BenefitMetricsExportParams,
  BenefitMetricsExportResponse,
  BenefitMetricsParams,
} from "../../models/benefitsMetrics";
import { API_ENDPOINTS } from "../../utils/api-service";
import {
  Benefit,
  BenefitCategoriesParams,
  BenefitCategory,
  BenefitCategoryInput,
  BenefitFeatured,
  BenefitFeaturedEvent,
  BenefitFeaturedEventsParams,
  BenefitFeaturedEventsResult,
  BenefitFeaturedParams,
  BenefitFeaturedResult,
  BenefitInput,
  BenefitLocation,
  BenefitLocationInput,
  BenefitPartner,
  BenefitPartnerInput,
  BenefitPartnersParams,
  BenefitSettings,
  BenefitSettingsInput,
  BenefitsParams,
  BenefitTenantScopedParams,
} from "../../models/benefits";
import { api, tenantContextHeader } from "./api";
import { parseContentDispositionFilename } from "../../utils/functions/downloadBlob";

const benefitsMetricsListTag = { type: "benefitsMetrics" as const, id: "LIST" };

const benefitsMetricsPartnerTag = (partnerId: string) => ({
  type: "benefitsMetrics" as const,
  id: partnerId,
});

const DEFAULT_BENEFIT_METRICS_FILENAME = "canjes-beneficios.csv";

/** Un solo mapeo para la query y el export: si divergen, el CSV deja de coincidir con la pantalla. */
const benefitMetricsQueryParams = (
  params: BenefitMetricsParams | void
): Record<string, string> => ({
  ...(params?.dateFrom ? { date_from: params.dateFrom } : {}),
  ...(params?.dateTo ? { date_to: params.dateTo } : {}),
  ...(params?.partnerId ? { partner_id: params.partnerId } : {}),
  ...(params?.tenantId ? { tenant_id: params.tenantId } : {}),
});

const isBenefitMetricsExportResponse = (
  result: unknown
): result is BenefitMetricsExportResponse => {
  if (!result || typeof result !== "object") {
    return false;
  }
  const value = result as { status?: unknown; blob?: unknown };
  return (
    value.status === 204 || (value.status === 200 && value.blob instanceof Blob)
  );
};

const unwrapBenefitMetrics = (response: unknown): BenefitMetrics => {
  if (typeof response === "object" && response !== null) {
    const record = response as Record<string, unknown>;
    if (record.success === true && record.data !== undefined) {
      return record.data as BenefitMetrics;
    }
  }

  return response as BenefitMetrics;
};

const settingsTag = { type: "benefitSettings" as const, id: "SETTINGS" };
const featuredEventTag = (id: string) => ({ type: "benefitFeaturedEvents" as const, id });
const featuredEventListTag = { type: "benefitFeaturedEvents" as const, id: "LIST" };
const featuredBenefitTag = (id: string) => ({ type: "benefitFeatured" as const, id });
const featuredBenefitListTag = { type: "benefitFeatured" as const, id: "LIST" };
const categoryTag = (id: string) => ({ type: "benefitCategories" as const, id });
const categoryListTag = { type: "benefitCategories" as const, id: "LIST" };
const partnerTag = (id: string) => ({ type: "benefitPartners" as const, id });
const partnerListTag = { type: "benefitPartners" as const, id: "LIST" };
const locationListTag = (partnerId: string) => ({
  type: "benefitLocations" as const,
  id: partnerId,
});
const benefitTag = (id: string) => ({ type: "benefits" as const, id });
const benefitListTag = { type: "benefits" as const, id: "LIST" };

/**
 * Los endpoints de partners y beneficios reciben multipart/form-data (upload
 * inline de imágenes). Este marcador hace que prepareHeaders NO fuerce
 * Content-Type: application/json y deje que el navegador setee el boundary.
 */
const MULTIPART_HEADERS = { "Content-Type": "multipart/form-data" };

/**
 * MT-011 — Tenant sobre el que se ejecuta la mutación multipart (header
 * X-Tenant-Context). Se llama `contextTenantId` para no chocar con el `tenantId`
 * del body, que el backend solo honra en el alta.
 */
interface BenefitMutationContext {
  contextTenantId?: string | null;
}

const appendField = (form: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return;
  if (typeof value === "boolean") {
    form.append(key, value ? "true" : "false");
  } else {
    form.append(key, String(value));
  }
};

/**
 * Campo que se puede borrar al editar: `undefined` no se envía (sin cambios) y
 * `null` viaja como "" para que el backend lo guarde como NULL.
 */
const appendClearable = (form: FormData, key: string, value: unknown) => {
  if (value === undefined) return;
  form.append(key, value === null ? "" : String(value));
};

/** Igual que appendClearable, para listas que viajan como JSON. */
const appendClearableList = (form: FormData, key: string, value: unknown[] | null | undefined) => {
  if (value === undefined) return;
  form.append(key, value === null || value.length === 0 ? "" : JSON.stringify(value));
};

const buildPartnerFormData = (input: BenefitPartnerInput): FormData => {
  const form = new FormData();
  appendField(form, "name", input.name);
  appendField(form, "slug", input.slug);
  appendField(form, "description", input.description);
  appendField(form, "categoryId", input.categoryId);
  appendClearable(form, "whatsappNumber", input.whatsappNumber);
  appendField(form, "instagramHandle", input.instagramHandle);
  appendClearable(form, "contactEmail", input.contactEmail);
  appendField(form, "featured", input.featured);
  appendField(form, "status", input.status);
  appendField(form, "sortOrder", input.sortOrder);
  appendField(form, "tenantId", input.tenantId);
  if (input.logo) form.append("logo", input.logo);
  if (input.coverImage) form.append("cover_image", input.coverImage);
  appendClearable(form, "logoUrl", input.logoUrl);
  appendClearable(form, "coverImageUrl", input.coverImageUrl);
  return form;
};

const buildBenefitFormData = (input: BenefitInput): FormData => {
  const form = new FormData();
  appendField(form, "partnerId", input.partnerId);
  appendField(form, "title", input.title);
  appendField(form, "description", input.description);
  appendField(form, "discountValue", input.discountValue);
  appendClearable(form, "discountType", input.discountType);
  appendField(form, "discountLabel", input.discountLabel);
  appendField(form, "promoCode", input.promoCode);
  appendField(form, "ctaMode", input.ctaMode);
  appendField(form, "ctaLabel", input.ctaLabel);
  appendField(form, "ctaInfoText", input.ctaInfoText);
  appendField(form, "termsAndConditions", input.termsAndConditions);
  if (input.redemptionMethods !== undefined) {
    form.append("redemptionMethods", JSON.stringify(input.redemptionMethods));
  }
  appendField(form, "logoUrl", input.logoUrl);
  appendClearable(form, "redemptionImageUrl", input.redemptionImageUrl);
  appendClearable(form, "startDate", input.startDate);
  appendClearable(form, "endDate", input.endDate);
  appendField(form, "status", input.status);
  appendField(form, "priority", input.priority);
  appendClearableList(form, "validDays", input.validDays);
  appendClearable(form, "validHoursStart", input.validHoursStart);
  appendClearable(form, "validHoursEnd", input.validHoursEnd);
  appendClearableList(form, "eligibleRoles", input.eligibleRoles);
  appendField(form, "tenantId", input.tenantId);
  (input.images ?? []).forEach((file) => form.append("images", file));
  if (input.imageUrls !== undefined && input.imageUrls.length > 0) {
    form.append("imageUrls", JSON.stringify(input.imageUrls));
  }
  return form;
};

/** Clave de caché estable: omite filtros vacíos (mismo criterio que venues). */
export const normalizeBenefitPartnersParams = (
  params: BenefitPartnersParams = {}
): Record<string, string> => {
  const normalized: Record<string, string> = {};
  if (params.categoryId) normalized.category_id = params.categoryId;
  if (params.status) normalized.status = params.status;
  if (params.featured !== undefined) normalized.featured = String(params.featured);
  return normalized;
};

export const normalizeBenefitsParams = (
  params: BenefitsParams = {}
): Record<string, string> => {
  const normalized: Record<string, string> = {};
  if (params.partnerId) normalized.partner_id = params.partnerId;
  if (params.categoryId) normalized.category_id = params.categoryId;
  if (params.status) normalized.status = params.status;
  if (params.featured !== undefined) normalized.featured = String(params.featured);
  return normalized;
};

export const benefitsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBenefitMetrics: build.query<BenefitMetrics, BenefitMetricsParams | void>({
      query: (params) => ({
        url: API_ENDPOINTS.BENEFITS.METRICS,
        params: benefitMetricsQueryParams(params),
      }),
      transformResponse: unwrapBenefitMetrics,
      // Placeholder al volver a la pantalla; el refetch sigue activo.
      keepUnusedDataFor: 300,
      providesTags: (_result, _error, params) => [
        benefitsMetricsListTag,
        ...(params?.partnerId ? [benefitsMetricsPartnerTag(params.partnerId)] : []),
      ],
    }),

    exportBenefitMetrics: build.mutation<
      BenefitMetricsExportResponse,
      BenefitMetricsExportParams
    >({
      query: (params) => ({
        url: API_ENDPOINTS.BENEFITS.METRICS_EXPORT,
        params: benefitMetricsQueryParams(params),
        // El responseHandler devuelve el error en vez de tirarlo: si lanza, fetchBaseQuery
        // lo envuelve en PARSING_ERROR y se pierden el status y el mensaje del backend.
        // Con validateStatus, el error queda con la misma forma { status, data } que el
        // resto de la app y getApiErrorMessage lo entiende.
        responseHandler: async (response) => {
          if (response.status === 204) {
            return { status: 204 as const };
          }

          const contentType = response.headers.get("Content-Type") || "";

          if (!response.ok) {
            return contentType.includes("json")
              ? ((await response.json()) as unknown)
              : { message: `Error al exportar (${response.status})` };
          }

          if (!contentType.includes("csv") && !contentType.includes("octet-stream")) {
            return { message: `Tipo de contenido inesperado: ${contentType}` };
          }

          return {
            status: 200 as const,
            blob: await response.blob(),
            filename:
              parseContentDispositionFilename(
                response.headers.get("Content-Disposition")
              ) ?? DEFAULT_BENEFIT_METRICS_FILENAME,
          };
        },
        validateStatus: (response, result) =>
          response.ok && isBenefitMetricsExportResponse(result),
      }),
    }),

    getBenefitSettings: build.query<BenefitSettings, void>({
      query: () => ({ url: "/bo/benefits/settings" }),
      providesTags: [settingsTag],
    }),

    updateBenefitSettings: build.mutation<BenefitSettings, BenefitSettingsInput>({
      query: (body) => ({ url: "/bo/benefits/settings", method: "PUT", body }),
      invalidatesTags: [settingsTag],
    }),

    // ---- Eventos destacados de la pantalla "Beneficios" ----
    getBenefitFeaturedEvents: build.query<
      BenefitFeaturedEventsResult,
      BenefitFeaturedEventsParams | void
    >({
      query: (params) => ({
        url: "/bo/benefits/featured-events",
        params: { page: params?.page ?? 1, limit: params?.limit ?? 100 },
      }),
      transformResponse: (response: { data: BenefitFeaturedEventsResult }) =>
        response.data,
      providesTags: (result) =>
        result
          ? [...result.items.map(({ id }) => featuredEventTag(id)), featuredEventListTag]
          : [featuredEventListTag],
    }),

    createBenefitFeaturedEvent: build.mutation<BenefitFeaturedEvent, { eventId: number }>({
      query: (body) => ({ url: "/bo/benefits/featured-events", method: "POST", body }),
      invalidatesTags: [featuredEventListTag],
    }),

    reorderBenefitFeaturedEvents: build.mutation<{ success: boolean }, string[]>({
      query: (featuredEventIds) => ({
        url: "/bo/benefits/featured-events/reorder",
        method: "PUT",
        body: { featuredEventIds },
      }),
      invalidatesTags: [featuredEventListTag],
    }),

    toggleBenefitFeaturedEventActive: build.mutation<
      BenefitFeaturedEvent,
      { id: string; active: boolean }
    >({
      query: ({ id, active }) => ({
        url: `/bo/benefits/featured-events/${id}`,
        method: "PATCH",
        body: { active },
      }),
      invalidatesTags: (_r, _e, { id }) => [featuredEventTag(id), featuredEventListTag],
    }),

    deleteBenefitFeaturedEvent: build.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/bo/benefits/featured-events/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [featuredEventTag(id), featuredEventListTag],
    }),

    // ---- Beneficios destacados de la pantalla "Beneficios" (tenant_benefit_featured) ----
    getBenefitFeatured: build.query<BenefitFeaturedResult, BenefitFeaturedParams | void>({
      query: (params) => ({
        url: "/bo/benefits/featured-benefits",
        params: { page: params?.page ?? 1, limit: params?.limit ?? 100 },
      }),
      transformResponse: (response: { data: BenefitFeaturedResult }) => response.data,
      providesTags: (result) =>
        result
          ? [...result.items.map(({ id }) => featuredBenefitTag(id)), featuredBenefitListTag]
          : [featuredBenefitListTag],
    }),

    createBenefitFeatured: build.mutation<BenefitFeatured, { benefitId: string }>({
      query: (body) => ({ url: "/bo/benefits/featured-benefits", method: "POST", body }),
      invalidatesTags: [featuredBenefitListTag],
    }),

    reorderBenefitFeatured: build.mutation<{ success: boolean }, string[]>({
      query: (featuredBenefitIds) => ({
        url: "/bo/benefits/featured-benefits/reorder",
        method: "PUT",
        body: { featuredBenefitIds },
      }),
      invalidatesTags: [featuredBenefitListTag],
    }),

    toggleBenefitFeaturedActive: build.mutation<
      BenefitFeatured,
      { id: string; active: boolean }
    >({
      query: ({ id, active }) => ({
        url: `/bo/benefits/featured-benefits/${id}`,
        method: "PATCH",
        body: { active },
      }),
      invalidatesTags: (_r, _e, { id }) => [featuredBenefitTag(id), featuredBenefitListTag],
    }),

    deleteBenefitFeatured: build.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/bo/benefits/featured-benefits/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [featuredBenefitTag(id), featuredBenefitListTag],
    }),

    // ---- Categorías (BN-003 / BN-037) ----
    // MT-011 — El tenant destino viaja en el header X-Tenant-Context: sin él, en
    // scope global el backend caía silenciosamente al tenant del admin (y después
    // fallaba con BENEFIT_CATEGORY_NOT_FOUND al asociarla). `tenantId` forma parte
    // del arg, así que RTK Query cachea una lista por tenant.
    getBenefitCategories: build.query<BenefitCategory[], BenefitCategoriesParams>({
      query: ({ tenantId }) => ({
        url: "/bo/benefits/categories",
        headers: tenantContextHeader(tenantId),
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => categoryTag(id)), categoryListTag]
          : [categoryListTag],
    }),

    createBenefitCategory: build.mutation<
      BenefitCategory,
      BenefitCategoryInput & BenefitTenantScopedParams
    >({
      query: ({ tenantId, ...body }) => ({
        url: "/bo/benefits/categories",
        method: "POST",
        body,
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: [categoryListTag],
    }),

    updateBenefitCategory: build.mutation<
      BenefitCategory,
      { id: string } & BenefitCategoryInput & BenefitTenantScopedParams
    >({
      query: ({ id, tenantId, ...body }) => ({
        url: `/bo/benefits/categories/${id}`,
        method: "PUT",
        body,
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_r, _e, { id }) => [categoryTag(id), categoryListTag],
    }),

    deleteBenefitCategory: build.mutation<
      { success: boolean },
      { id: string } & BenefitTenantScopedParams
    >({
      query: ({ id, tenantId }) => ({
        url: `/bo/benefits/categories/${id}`,
        method: "DELETE",
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_r, _e, { id }) => [categoryTag(id), categoryListTag],
    }),

    // ---- Partners (BN-004 / BN-038) ----
    getBenefitPartners: build.query<BenefitPartner[], BenefitPartnersParams>({
      query: (params) => ({
        url: "/bo/benefits/partners",
        params: normalizeBenefitPartnersParams(params),
        headers: tenantContextHeader(params?.tenantId),
      }),
      // El tenant no es query param (va por header) pero sí forma parte de la
      // clave de caché: si no, dos tenants compartirían la misma lista.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${JSON.stringify({
          ...normalizeBenefitPartnersParams(queryArgs),
          tenant: queryArgs?.tenantId ?? null,
        })})`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => partnerTag(id)), partnerListTag]
          : [partnerListTag],
    }),

    // Orden de los partners del tenant elegido en el header (todos los ids, en el
    // orden nuevo). Define en qué posición salen sus beneficios en la app.
    reorderBenefitPartners: build.mutation<{ success: boolean }, string[]>({
      query: (partnerIds) => ({
        url: "/bo/benefits/partners/reorder",
        method: "PUT",
        body: { partnerIds },
      }),
      invalidatesTags: [partnerListTag],
    }),

    getBenefitPartnerById: build.query<BenefitPartner, string>({
      query: (id) => ({ url: `/bo/benefits/partners/${id}` }),
      providesTags: (_r, _e, id) => [partnerTag(id)],
    }),

    // `contextTenantId` es el tenant sobre el que se opera (header). No se
    // confunde con `tenantId` del body, que el backend solo honra en el alta.
    createBenefitPartner: build.mutation<
      BenefitPartner,
      BenefitPartnerInput & BenefitMutationContext
    >({
      query: ({ contextTenantId, ...input }) => ({
        url: "/bo/benefits/partners",
        method: "POST",
        body: buildPartnerFormData(input),
        headers: { ...MULTIPART_HEADERS, ...tenantContextHeader(contextTenantId) },
      }),
      invalidatesTags: [partnerListTag],
    }),

    updateBenefitPartner: build.mutation<
      BenefitPartner,
      { id: string } & BenefitPartnerInput & BenefitMutationContext
    >({
      query: ({ id, contextTenantId, ...input }) => ({
        url: `/bo/benefits/partners/${id}`,
        method: "PUT",
        body: buildPartnerFormData(input),
        headers: { ...MULTIPART_HEADERS, ...tenantContextHeader(contextTenantId) },
      }),
      invalidatesTags: (_r, _e, { id }) => [partnerTag(id), partnerListTag],
    }),

    deleteBenefitPartner: build.mutation<
      { success: boolean },
      { id: string } & BenefitTenantScopedParams
    >({
      query: ({ id, tenantId }) => ({
        url: `/bo/benefits/partners/${id}`,
        method: "DELETE",
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_r, _e, { id }) => [partnerTag(id), partnerListTag],
    }),

    // ---- Ubicaciones de partner (BN-006 / BN-039) ----
    // Operan sobre un partner existente: el tenant es siempre el del partner.
    getBenefitPartnerLocations: build.query<
      BenefitLocation[],
      { partnerId: string } & BenefitTenantScopedParams
    >({
      query: ({ partnerId, tenantId }) => ({
        url: `/bo/benefits/partners/${partnerId}/locations`,
        headers: tenantContextHeader(tenantId),
      }),
      providesTags: (_r, _e, { partnerId }) => [locationListTag(partnerId)],
    }),

    createBenefitPartnerLocation: build.mutation<
      BenefitLocation,
      { partnerId: string } & BenefitLocationInput & BenefitTenantScopedParams
    >({
      query: ({ partnerId, tenantId, ...body }) => ({
        url: `/bo/benefits/partners/${partnerId}/locations`,
        method: "POST",
        body,
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_r, _e, { partnerId }) => [locationListTag(partnerId)],
    }),

    updateBenefitPartnerLocation: build.mutation<
      BenefitLocation,
      { partnerId: string; id: string } & BenefitLocationInput &
        BenefitTenantScopedParams
    >({
      query: ({ partnerId, id, tenantId, ...body }) => ({
        url: `/bo/benefits/partners/${partnerId}/locations/${id}`,
        method: "PUT",
        body,
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_r, _e, { partnerId }) => [locationListTag(partnerId)],
    }),

    deleteBenefitPartnerLocation: build.mutation<
      { success: boolean },
      { partnerId: string; id: string } & BenefitTenantScopedParams
    >({
      query: ({ partnerId, id, tenantId }) => ({
        url: `/bo/benefits/partners/${partnerId}/locations/${id}`,
        method: "DELETE",
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_r, _e, { partnerId }) => [locationListTag(partnerId)],
    }),

    // ---- Beneficios (BN-005 / BN-040) ----
    getBenefits: build.query<Benefit[], BenefitsParams>({
      query: (params) => ({
        url: "/bo/benefits",
        params: normalizeBenefitsParams(params),
        headers: tenantContextHeader(params?.tenantId),
      }),
      // El tenant va por header pero forma parte de la clave de caché.
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${JSON.stringify({
          ...normalizeBenefitsParams(queryArgs),
          tenant: queryArgs?.tenantId ?? null,
        })})`,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => benefitTag(id)), benefitListTag]
          : [benefitListTag],
    }),

    getBenefitById: build.query<Benefit, string>({
      query: (id) => ({ url: `/bo/benefits/${id}` }),
      providesTags: (_r, _e, id) => [benefitTag(id)],
    }),

    createBenefit: build.mutation<Benefit, BenefitInput & BenefitMutationContext>({
      query: ({ contextTenantId, ...input }) => ({
        url: "/bo/benefits",
        method: "POST",
        body: buildBenefitFormData(input),
        headers: { ...MULTIPART_HEADERS, ...tenantContextHeader(contextTenantId) },
      }),
      invalidatesTags: [benefitListTag],
    }),

    updateBenefit: build.mutation<
      Benefit,
      { id: string } & BenefitInput & BenefitMutationContext
    >({
      query: ({ id, contextTenantId, ...input }) => ({
        url: `/bo/benefits/${id}`,
        method: "PUT",
        body: buildBenefitFormData(input),
        headers: { ...MULTIPART_HEADERS, ...tenantContextHeader(contextTenantId) },
      }),
      invalidatesTags: (_r, _e, { id }) => [benefitTag(id), benefitListTag],
    }),

    reorderBenefitImages: build.mutation<
      Benefit,
      { benefitId: string; imageIds: string[] }
    >({
      query: ({ benefitId, imageIds }) => ({
        url: `/bo/benefits/${benefitId}/images/reorder`,
        method: "PUT",
        body: { imageIds },
      }),
      invalidatesTags: (_r, _e, { benefitId }) => [
        benefitTag(benefitId),
        benefitListTag,
      ],
    }),

    deleteBenefitImage: build.mutation<
      Benefit,
      { benefitId: string; imageId: string }
    >({
      query: ({ benefitId, imageId }) => ({
        url: `/bo/benefits/${benefitId}/images/${imageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { benefitId }) => [
        benefitTag(benefitId),
        benefitListTag,
      ],
    }),

    deleteBenefit: build.mutation<
      { success: boolean },
      { id: string } & BenefitTenantScopedParams
    >({
      query: ({ id, tenantId }) => ({
        url: `/bo/benefits/${id}`,
        method: "DELETE",
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: (_r, _e, { id }) => [benefitTag(id), benefitListTag],
    }),

    duplicateBenefit: build.mutation<Benefit, { id: string } & BenefitTenantScopedParams>({
      query: ({ id, tenantId }) => ({
        url: `/bo/benefits/${id}/duplicate`,
        method: "POST",
        headers: tenantContextHeader(tenantId),
      }),
      invalidatesTags: [benefitListTag],
    }),
  }),
});

export const {
  useGetBenefitMetricsQuery,
  useExportBenefitMetricsMutation,
  useGetBenefitSettingsQuery,
  useUpdateBenefitSettingsMutation,
  useGetBenefitFeaturedEventsQuery,
  useCreateBenefitFeaturedEventMutation,
  useReorderBenefitFeaturedEventsMutation,
  useToggleBenefitFeaturedEventActiveMutation,
  useDeleteBenefitFeaturedEventMutation,
  useGetBenefitFeaturedQuery,
  useCreateBenefitFeaturedMutation,
  useReorderBenefitFeaturedMutation,
  useReorderBenefitPartnersMutation,
  useToggleBenefitFeaturedActiveMutation,
  useDeleteBenefitFeaturedMutation,
  useGetBenefitCategoriesQuery,
  useCreateBenefitCategoryMutation,
  useUpdateBenefitCategoryMutation,
  useDeleteBenefitCategoryMutation,
  useGetBenefitPartnersQuery,
  useGetBenefitPartnerByIdQuery,
  useCreateBenefitPartnerMutation,
  useUpdateBenefitPartnerMutation,
  useDeleteBenefitPartnerMutation,
  useGetBenefitPartnerLocationsQuery,
  useCreateBenefitPartnerLocationMutation,
  useUpdateBenefitPartnerLocationMutation,
  useDeleteBenefitPartnerLocationMutation,
  useGetBenefitsQuery,
  useGetBenefitByIdQuery,
  useCreateBenefitMutation,
  useUpdateBenefitMutation,
  useReorderBenefitImagesMutation,
  useDeleteBenefitImageMutation,
  useDeleteBenefitMutation,
  useDuplicateBenefitMutation,
} = benefitsApi;
